import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ReactionsGroupedByType from 'owa-service/lib/contract/ReactionsGroupedByType';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import {
    useLivePersonaCard,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';
import { TooltipHost, TooltipDelay, ITooltipProps } from '@fluentui/react/lib/Tooltip';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { useId } from '@uifabric/react-hooks';
import { Reaction_ReactorsReaction_Title } from '../utils/reactions.locstring.json';
import loc, { format } from 'owa-localize';
import { ReactionsMap } from '../utils/reactionsData';
import getReactionResourceData from '../utils/getReactionResourceData';
import * as lazyTriageActions from 'owa-mail-triage-action';
import {
    Reaction_SetAnnouncement_Text,
    Reaction_UnsetAnnouncement_Text,
} from '../utils/reactions.locstring.json';
import Message from 'owa-service/lib/contract/Message';
import { ClientItemId } from 'owa-client-ids';
import { InstrumentationContext } from 'owa-mail-store';

const styles = require('./Reactions.scss');
const classNames = require('classnames');

export interface ReactionInfoButtonProps {
    ownerReactionType: string;
    reactionInfo: ReactionsGroupedByType;
    conversationId: ClientItemId;
    itemId: ClientItemId;
    message: Message;
    instrumentationContext: InstrumentationContext;
}

export const ReactionInfoButton = observer(function ReactionInfoButton(
    props: ReactionInfoButtonProps
) {
    const tooltipId = useId('reactorsInfoTooltip');
    const calloutProps = {
        isBeakVisible: false,
        gapSpace: 2,
    };

    const resourceData = getReactionResourceData(
        props.reactionInfo.ReactionType,
        false /* isAnimated */
    );

    const onReaction = (event?: React.MouseEvent<{}>) => {
        const announcementString = format(
            props.ownerReactionType === props.reactionInfo.ReactionType
                ? loc(Reaction_UnsetAnnouncement_Text)
                : loc(Reaction_SetAnnouncement_Text),
            ReactionsMap[props.reactionInfo.ReactionType].localizedName
        );

        lazyTriageActions.lazyReactToItem.importAndExecute(
            props.message,
            props.itemId,
            props.conversationId,
            props.reactionInfo.ReactionType,
            props.instrumentationContext,
            announcementString,
            props.ownerReactionType
        );
    };

    const tooltipProps: ITooltipProps = {
        onRenderContent: () => (
            <div className={classNames(styles.scrollContainer, 'customScrollBar')}>
                <div className={styles.reactionTitle}>
                    {resourceData && (
                        <img className={styles.reactionTitleIcon} src={resourceData} />
                    )}
                    {!resourceData && (
                        <span className={styles.reactionTitleIcon}>
                            {ReactionsMap[props.reactionInfo.ReactionType].icon}
                        </span>
                    )}
                    <div className={styles.reactionLabel}>
                        {format(
                            loc(Reaction_ReactorsReaction_Title),
                            ReactionsMap[props.reactionInfo.ReactionType].localizedName
                        )}
                    </div>
                </div>

                <div className={styles.personaCollection}>
                    {props.reactionInfo.Reactors.map(reactor => {
                        const emailAddress = reactor.ReactorEmail.EmailAddress;
                        return (
                            <LPCWrapper
                                key={emailAddress}
                                emailAddress={emailAddress}
                                name={reactor.ReactorEmail.Name}
                                mailBoxType={reactor.ReactorEmail.MailboxType}
                                disableHover={true}
                                clientScenario="Reactor">
                                <PersonaControl
                                    className={styles.persona}
                                    key={emailAddress}
                                    name={reactor.ReactorEmail.Name}
                                    emailAddress={emailAddress}
                                    size={PersonaSize.size32}
                                    showPersonaDetails={true}
                                    mailboxType={reactor.ReactorEmail.MailboxType}
                                    onRenderPrimaryText={onRenderPrimaryText(
                                        reactor.ReactorEmail.Name
                                    )}
                                />
                            </LPCWrapper>
                        );
                    })}
                </div>
            </div>
        ),
    };

    const onRenderPrimaryText = (name: string) => {
        return (props: IPersonaProps): JSX.Element => {
            return <>{name}</>;
        };
    };

    const buttonStyle = classNames([
        styles.reactionInfoButton,
        props.reactionInfo.ReactionType === props.ownerReactionType &&
            styles.reactionInfoButtonOwnerReacted,
    ]);

    return (
        <TooltipHost
            tooltipProps={tooltipProps}
            delay={TooltipDelay.zero}
            id={tooltipId}
            styles={{ root: styles.toolTipHost }}
            calloutProps={calloutProps}>
            <div className={buttonStyle} onClick={onReaction}>
                {resourceData && <img className={styles.reactionInfoIcon} src={resourceData} />}
                {!resourceData && (
                    <span className={styles.reactionInfoIcon}>
                        {ReactionsMap[props.reactionInfo.ReactionType].icon}
                    </span>
                )}
                <span className={styles.reactionInfoLabel}>{props.reactionInfo.Count}</span>
            </div>
        </TooltipHost>
    );
});

function LPCWrapper(props: PersonaCardBehaviorProps & { children: React.ReactNode }) {
    const PersonaCardBehavior = useLivePersonaCard(props);
    return <PersonaCardBehavior>{props.children}</PersonaCardBehavior>;
}
